import { useMemo } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { aggregateByParentCategory } from '@/lib/statistics'
import { DEFAULT_CATEGORY_COLOR, UNCATEGORIZED_LABEL } from '@/lib/constants'
import { cn, formatAmount } from '@/lib/utils'
import type { Transaction } from '@/lib/hooks'
import type { Category } from '@/lib/hooks'

interface SpendingBreakdownProps {
  transactions: Transaction[]
  categories: Category[]
  onSelectCategory?: (name: string) => void
}

export function SpendingBreakdown({ transactions, categories, onSelectCategory }: SpendingBreakdownProps) {
  const { rows, total, getColor } = useMemo(() => {
    const colorMap = new Map<string, string>()
    for (const cat of categories) {
      if (cat.parent_id === null && cat.color) colorMap.set(cat.name, cat.color)
    }
    const resolveColor = (name: string) =>
      name === UNCATEGORIZED_LABEL
        ? DEFAULT_CATEGORY_COLOR
        : colorMap.get(name) ?? DEFAULT_CATEGORY_COLOR

    const totals = aggregateByParentCategory(transactions)
    const arr = Array.from(totals.entries())
      .map(([name, amount]) => ({ name, amount }))
      .filter((r) => r.amount > 0)
      .sort((a, b) => b.amount - a.amount)
    const sum = arr.reduce((s, r) => s + r.amount, 0)
    return { rows: arr, total: sum, getColor: resolveColor }
  }, [transactions, categories])

  if (rows.length === 0 || total === 0) return null

  return (
    <Card>
      <CardHeader>
        <CardTitle>カテゴリ別支出</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* 100% stacked horizontal bar */}
        <div className="flex h-2 w-full gap-0.5 overflow-hidden rounded-full bg-muted">
          {rows.map((r) => {
            const pct = (r.amount / total) * 100
            return (
              <div
                key={r.name}
                style={{
                  width: `${pct}%`,
                  minWidth: pct > 0 ? '2px' : undefined,
                  backgroundColor: getColor(r.name),
                }}
                title={`${r.name}: ${formatAmount(r.amount)} (${pct.toFixed(1)}%)`}
              />
            )
          })}
        </div>

        {/* Legend */}
        <ul className="space-y-1">
          {rows.map((r) => {
            const pct = (r.amount / total) * 100
            const interactive = !!onSelectCategory
            return (
              <li
                key={r.name}
                className={cn(
                  'grid grid-cols-[1fr_auto_auto] items-center gap-3 rounded-md px-1 py-1 text-sm',
                  interactive && 'cursor-pointer -mx-1 px-2 active:bg-muted/50 transition-colors',
                )}
                onClick={interactive ? () => onSelectCategory(r.name) : undefined}
              >
                <span className="flex min-w-0 items-center gap-1.5">
                  <span
                    className="inline-block size-2 shrink-0 rounded-full"
                    style={{ backgroundColor: getColor(r.name) }}
                    aria-hidden
                  />
                  <span className="truncate font-medium">{r.name}</span>
                </span>
                <span className="text-xs tabular-nums text-muted-foreground">
                  {pct.toFixed(1)}%
                </span>
                <span className="min-w-[64px] text-right tabular-nums">
                  {formatAmount(r.amount)}
                </span>
              </li>
            )
          })}
        </ul>

        <div className="flex items-center justify-between border-t pt-2 text-sm">
          <span className="text-muted-foreground">合計</span>
          <span className="font-medium tabular-nums">{formatAmount(total)}</span>
        </div>
      </CardContent>
    </Card>
  )
}
